import * as React from 'react';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import { POOL_SIZE, flameOpacity, flamePath, spawnDelay, spawnFlame, type Flame, type Size } from './flameGeometry';

/** Fade of the whole field when hover starts and ends, in ms. */
const FADE_MS = 220;

export interface ZuulFlamesProps {
  /** Runs the animation while true; the field fades out and the loop stops when it goes false. */
  active: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Flames licking up behind the logo on hover, drawn as SVG paths rebuilt each frame — the approach
 * ste-vg's "SVG Fire" pen takes, minus its Snap.svg and GSAP dependencies. Replaces a 777 KB
 * animated webp, and being paths rather than pixels it takes the theme preset's colors.
 *
 * The paths are a fixed pool: the loop only rewrites `d` and `opacity` on elements React already
 * rendered, so a frame costs no reconciliation and no DOM churn. It runs only while `active`, and
 * not at all under `prefers-reduced-motion`.
 *
 * Mount it in the box the fire should fill — flame sizes are fractions of that box's height. In
 * the sidebar that is the toolbar, whose full height leaves clear space under the logo; the logo's
 * own box is tight around the artwork and leaves the fire nowhere to sit but on top of it.
 */
export default function ZuulFlames({ active, sx }: ZuulFlamesProps) {
  const theme = useTheme();
  const svgRef = React.useRef<SVGSVGElement>(null);
  const pathsRef = React.useRef<(SVGPathElement | null)[]>([]);
  const [size, setSize] = React.useState<Size>({ width: 0, height: 0 });

  // An animated hover is exactly the kind of motion this setting asks us to drop.
  const [reduceMotion, setReduceMotion] = React.useState(false);
  React.useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduceMotion(query.matches);
    const onChange = (event: MediaQueryListEvent) => setReduceMotion(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  // 1 SVG user unit = 1 CSS pixel, so flames keep their proportions in any shaped box.
  React.useEffect(() => {
    const svg = svgRef.current;
    if (!svg || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0]?.contentRect ?? svg.getBoundingClientRect();
      setSize((current) => (current.width === width && current.height === height ? current : { width, height }));
    });
    observer.observe(svg);
    return () => observer.disconnect();
  }, []);

  const running = active && !reduceMotion && size.width > 0;

  React.useEffect(() => {
    if (!running) return;
    // The pool array itself is stable for the component's life, so it is safe to hold onto here.
    const paths = pathsRef.current;
    const flames: (Flame | null)[] = Array.from({ length: POOL_SIZE }, () => null);
    let frame = 0;
    // Staggered so the fire builds rather than igniting as one block.
    let nextSpawn = 0;

    const draw = (now: number) => {
      if (now >= nextSpawn) {
        const slot = flames.indexOf(null);
        if (slot !== -1) flames[slot] = spawnFlame(now, size);
        nextSpawn = now + spawnDelay();
      }

      for (let i = 0; i < flames.length; i++) {
        const path = paths[i];
        const flame = flames[i];
        if (!path) continue;
        if (!flame) {
          path.removeAttribute('d');
          continue;
        }
        const t = (now - flame.bornAt) / flame.life;
        if (t >= 1) {
          flames[i] = null;
          path.removeAttribute('d');
          continue;
        }
        path.setAttribute('d', flamePath(flame, t, size));
        path.setAttribute('opacity', flameOpacity(flame, t).toFixed(3));
      }
      frame = requestAnimationFrame(draw);
    };

    frame = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(frame);
      paths.forEach((path) => path?.removeAttribute('d'));
    };
  }, [running, size]);

  // CSS variables keep the ramp scheme-aware; color-mix lifts a hot core off the preset itself.
  const palette = (theme.vars ?? theme).palette;
  const ramp = [
    palette.primary.main,
    `color-mix(in srgb, ${palette.primary.main} 80%, white)`,
    palette.primary.main,
    `color-mix(in srgb, ${palette.secondary.main} 80%, white)`,
    palette.primary.main,
    `color-mix(in srgb, ${palette.primary.main} 55%, white)`,
  ];

  return (
    <Box
      component='svg'
      ref={svgRef}
      aria-hidden
      focusable='false'
      viewBox={`0 0 ${size.width || 1} ${size.height || 1}`}
      sx={[
        {
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          opacity: running ? 1 : 0,
          transition: `opacity ${FADE_MS}ms ease`,
          // Overlapping flames should build toward the hot core: lighter on a dark scheme, denser
          // on a light one, since `screen` over near-white paper would wash the fire away.
          mixBlendMode: 'multiply',
          ...theme.applyStyles('dark', { mixBlendMode: 'screen' }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {Array.from({ length: POOL_SIZE }, (_, i) => (
        <path
          key={i}
          ref={(node) => {
            pathsRef.current[i] = node;
          }}
          fill={ramp[i % ramp.length]}
        />
      ))}
    </Box>
  );
}
